import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import usePush from 'hooks/router/usePush';
import { FormattedMessage } from 'react-intl';
import { Icon } from 'components/HeroSemantic';

const REDIRECT_LINK = '/selectOwnerId';
const CHANGE_PASSWORD_LINK = '/user/changePassword';
const SECURITY_LINK = '/security';

const getStoredThemeMode = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 'device';
  }

  const storedTheme = window.localStorage.getItem('owner-theme');
  return storedTheme === 'dark' || storedTheme === 'light' ? storedTheme : 'device';
};

const getUsernameStyle = (inverted = false) => ({
  alignItems: 'center',
  background: 'transparent',
  border: '1px solid transparent',
  borderRadius: '999px',
  color: inverted ? '#ffffff' : 'var(--owner-text)',
  cursor: 'pointer',
  display: 'inline-flex',
  fontSize: '14px',
  fontWeight: 700,
  gap: '8px',
  lineHeight: '20px',
  minHeight: '40px',
  padding: '8px 12px',
  transition: 'background-color 160ms ease, border-color 160ms ease',
});

const getMenuStyle = (openUpward = false) => ({
  background: 'var(--owner-surface-raised)',
  border: '1px solid var(--owner-border)',
  borderRadius: '12px',
  boxShadow: 'var(--owner-shadow-strong)',
  minWidth: '250px',
  padding: '10px',
  position: 'absolute',
  right: 0,
  textAlign: 'left',
  ...(openUpward ? { bottom: 'calc(100% + 8px)' } : { top: 'calc(100% + 8px)' }),
  zIndex: 20,
});

const menuSectionStyle = {
  borderTop: '1px solid var(--owner-border)',
  marginTop: '10px',
  paddingTop: '10px',
};

const sectionLabelStyle = {
  color: 'var(--owner-text-muted)',
  display: 'block',
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '16px',
  margin: '0 0 8px',
  padding: '0 2px',
  textTransform: 'uppercase',
};

const themeGroupStyle = {
  background: 'var(--owner-surface-muted)',
  border: '1px solid var(--owner-border)',
  borderRadius: '10px',
  display: 'grid',
  gap: '4px',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  padding: '4px',
};

const getMenuItemStyle = (active = false) => ({
  alignItems: 'center',
  background: active ? 'var(--owner-accent-subtle)' : 'transparent',
  border: 0,
  borderRadius: '8px',
  color: active ? 'var(--owner-accent-readable)' : 'var(--owner-text)',
  cursor: 'pointer',
  display: 'flex',
  fontSize: '14px',
  fontWeight: active ? 700 : 500,
  gap: '10px',
  lineHeight: '20px',
  padding: '9px 10px',
  textAlign: 'left',
  width: '100%',
});

const getThemeButtonStyle = (active = false) => ({
  alignItems: 'center',
  background: active ? 'var(--owner-surface-raised)' : 'transparent',
  border: active ? '1px solid var(--owner-accent-border)' : '1px solid transparent',
  borderRadius: '8px',
  boxShadow: active ? 'var(--owner-shadow)' : 'none',
  color: active ? 'var(--owner-accent-readable)' : 'var(--owner-text-muted)',
  cursor: 'pointer',
  display: 'inline-flex',
  flexDirection: 'column',
  fontSize: '12px',
  fontWeight: active ? 700 : 600,
  gap: '2px',
  justifyContent: 'center',
  lineHeight: '16px',
  minHeight: '48px',
  padding: '6px 4px',
  textAlign: 'center',
  width: '100%',
});

const logoutItemStyle = {
  ...getMenuItemStyle(),
  color: 'var(--owner-danger)',
};

const UserStatus = ({ closeSidebar, float, inverted, logout, name, openUpward }) => {
  const auth = useSelector(state => state.auth);
  const { push } = usePush();
  const [menuOpen, setMenuOpen] = useState(false);
  const [themeMode, setThemeMode] = useState('device');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setThemeMode(getStoredThemeMode());

    const handleOutsidePointerDown = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsidePointerDown);
    document.addEventListener('touchstart', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown);
      document.removeEventListener('touchstart', handleOutsidePointerDown);
    };
  }, []);

  const isAdmin = auth.admin && typeof auth.admin === 'object' && Object.keys(auth.admin).length > 0;

  const handleChangePassword = () => {
    if (typeof closeSidebar === 'function') {closeSidebar();}
    setMenuOpen(false);
    push(CHANGE_PASSWORD_LINK);
  };

  const handleSecurity = () => {
    if (typeof closeSidebar === 'function') {closeSidebar();}
    setMenuOpen(false);
    push(SECURITY_LINK);
  };

  const handleSelectOwner = () => {
    setMenuOpen(false);
    push(REDIRECT_LINK);
  };

  const handleLogout = () => {
    if (typeof closeSidebar === 'function') {closeSidebar();}
    logout();
  };

  const handleThemeMode = mode => {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (mode === 'device') {
        window.localStorage.removeItem('owner-theme');
        document.documentElement.removeAttribute('data-theme');
      }
      else {
        window.localStorage.setItem('owner-theme', mode);
        document.documentElement.dataset.theme = mode;
      }
    }
    setThemeMode(mode);
    setMenuOpen(false);
  };

  const wrapperStyle = {
    float,
    margin: '20px',
    position: 'relative',
    textAlign: float,
  };

  return (
    <span ref={wrapperRef} style={wrapperStyle}>
      <button
        type="button"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={() => setMenuOpen(prev => !prev)}
        style={getUsernameStyle(inverted)}
      >
        <Icon name="user" />
        <span>{name}</span>
        <Icon name="dropdown" size="small" />
      </button>
      {menuOpen && (
        <div role="menu" style={getMenuStyle(openUpward)}>
          <button type="button" role="menuitem" style={getMenuItemStyle()} onClick={handleChangePassword}>
            <Icon name="lock" />
            <FormattedMessage id="page.user.changePassword.title" />
          </button>
          <button type="button" role="menuitem" style={getMenuItemStyle()} onClick={handleSecurity}>
            <Icon name="shield" />
            <FormattedMessage id="page.passkeys.title" defaultMessage="Security & Login" />
          </button>
          {
            isAdmin && (
              <button type="button" role="menuitem" style={getMenuItemStyle()} onClick={handleSelectOwner}>
                <Icon name="user" />
                <FormattedMessage id="page.login.action.selectOwner" />
              </button>
            )
          }
          <div style={menuSectionStyle}>
            <span style={sectionLabelStyle}>Appearance</span>
            <div role="group" aria-label="Appearance" style={themeGroupStyle}>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={themeMode === 'device'}
                style={getThemeButtonStyle(themeMode === 'device')}
                onClick={() => handleThemeMode('device')}
              >
                <Icon name="settings" size="small" />
                Device
              </button>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={themeMode === 'light'}
                style={getThemeButtonStyle(themeMode === 'light')}
                onClick={() => handleThemeMode('light')}
              >
                <Icon name="sun" size="small" />
                Light
              </button>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={themeMode === 'dark'}
                style={getThemeButtonStyle(themeMode === 'dark')}
                onClick={() => handleThemeMode('dark')}
              >
                <Icon name="moon" size="small" />
                Dark
              </button>
            </div>
          </div>
          <div style={menuSectionStyle}>
            <button
              type="button"
              role="menuitem"
              style={logoutItemStyle}
              onClick={handleLogout}
            >
              <Icon name="close" />
              <FormattedMessage id="page.login.action.logout" />
            </button>
          </div>
        </div>
      )}
    </span>
  );
};

UserStatus.propTypes = {
  closeSidebar: PropTypes.func,
  float: PropTypes.string,
  inverted: PropTypes.bool,
  logout: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  openUpward: PropTypes.bool,
};

UserStatus.defaultProps = {
  float: 'right',
  inverted: false,
  openUpward: false,
};

export default UserStatus;
